import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./context/AuthContext";
import { ThemeContextProvider } from "./context/ThemeContext";
import { Box, Typography, Button } from "@mui/material";
import Layout from "./components/Layout";
import Login from "./pages/Auth/Login";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import VuelosList from "./pages/Vuelos/VuelosList";
import VueloForm from "./pages/Vuelos/VueloForm";
import VueloDetail from "./pages/Vuelos/VueloDetail";
import VueloGestion from "./pages/Vuelos/VueloGestion";
import PalletForm from "./pages/Pallets/PalletForm";
import PalletLabel from "./pages/Pallets/PalletLabel";
import OficinaIndex from "./pages/Oficina/OficinaIndex";
import Agentes from "./pages/Oficina/Agentes";
import Clientes from "./pages/Oficina/Clientes";
import Shippers from "./pages/Oficina/Shippers";
import Consignatarios from "./pages/Oficina/Consignatarios";
import Reservas from "./pages/Oficina/Reservas";
import ReportesIndex from "./pages/Reportes/ReportesIndex";
import ReporteVuelos from "./pages/Reportes/ReporteVuelos";
import ReporteDestinos from "./pages/Reportes/ReporteDestinos";
import ReporteDG from "./pages/Reportes/ReporteDG";
import ReporteProductividad from "./pages/Reportes/ReporteProductividad";
import AdminIndex from "./pages/Admin/AdminIndex";
import Equipos from "./pages/Admin/Equipos";
import Rutas from "./pages/Admin/Rutas";
import Usuarios from "./pages/Admin/Usuarios";
import Manifiesto from "./pages/Oficina/Manifiesto";
import AerolineaIndex from "./pages/Aerolinea/AerolineaIndex";
import GestionVuelos from "./pages/Aerolinea/GestionVuelos";
import Estaciones from "./pages/Aerolinea/Estaciones";
import FlightBookings from "./pages/Aerolinea/FlightBookings";
import FacturacionIndex from "./pages/Facturacion/FacturacionIndex";
import PanelFacturacion from "./pages/Facturacion/PanelFacturacion";
import ClientesFacturacion from "./pages/Facturacion/ClientesFacturacion";
import WarehouseIndex from "./pages/Warehouse/WarehouseIndex";
import WarehouseReception from "./pages/Warehouse/Reception";
import WarehouseInventory from "./pages/Warehouse/Inventory";
import WarehouseLoading from "./pages/Warehouse/LoadingList";
import WarehouseDetail from "./pages/Warehouse/WarehouseDetail";
import AwbTracking from "./pages/Warehouse/AwbTracking";

// Configurar QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Componente 404
const NotFound = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 2,
      background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
    }}
  >
    <Typography variant="h1" sx={{ fontSize: '8rem', fontWeight: 'bold', color: 'white' }}>
      404
    </Typography>
    <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.8)' }}>
      Página no encontrada
    </Typography>
    <Button
      variant="contained"
      href="/vuelos"
      sx={{
        mt: 2,
        backgroundColor: 'white',
        color: 'primary.main',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.9)',
        },
      }}
    >
      Volver al inicio
    </Button>
  </Box>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContextProvider>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Ruta pública */}
              <Route path="/login" element={<Login />} />

              {/* Rutas protegidas - Vuelos */}
              <Route
                path="/vuelos"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <VuelosList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/nuevo"
                element={
                  <ProtectedRoute requiredPermission="vuelos.crear">
                    <Layout>
                      <VueloForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/editar"
                element={
                  <ProtectedRoute requiredPermission="vuelos.editar">
                    <Layout>
                      <VueloForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/detalle"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <VueloDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/gestionar"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <VueloGestion />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/nuevo-pallet"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PalletForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/editar-pallet/:palletId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PalletForm editMode={true} />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vuelos/:awb/pallet/:placa/etiqueta"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PalletLabel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rutas Oficina */}
              <Route
                path="/oficina"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <OficinaIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/agentes"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Agentes />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/clientes"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Clientes />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/shippers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Shippers />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/consignatarios"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Consignatarios />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/reservas"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Reservas />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/oficina/manifiesto"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Manifiesto />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rutas Reportes */}
              <Route
                path="/reportes"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportesIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reportes/vuelos"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReporteVuelos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reportes/destinos"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReporteDestinos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reportes/dangerous-goods"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReporteDG />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reportes/productividad"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReporteProductividad />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rutas Aerolínea */}
              <Route
                path="/aerolinea"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AerolineaIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/vuelos"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <GestionVuelos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/rutas"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Rutas />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/reservas"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Reservas />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/manifiesto"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Manifiesto />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/estaciones"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Estaciones />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/loading"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseLoading />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aerolinea/bookings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FlightBookings />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rutas Facturación */}
              <Route
                path="/facturacion"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FacturacionIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/facturacion/panel"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PanelFacturacion />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/facturacion/clientes"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ClientesFacturacion />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rutas Warehouse */}
              <Route
                path="/warehouse"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/warehouse/reception"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseReception />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/warehouse/reception/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseReception />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/warehouse/inventory"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseInventory />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/warehouse/loading"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WarehouseLoading />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/warehouse/tracking"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AwbTracking />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Ruta pública - accesible via QR sin login */}
              <Route
                path="/warehouse/detail/:whId"
                element={<WarehouseDetail />}
              />

              {/* Rutas Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AdminIndex />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/equipos"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Equipos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/rutas"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Rutas />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/usuarios"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <Usuarios />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Redirect raíz */}
              <Route path="/" element={<Navigate to="/vuelos" replace />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeContextProvider>
    </QueryClientProvider>
  );
}

export default App;
